import { z } from 'zod';
import { PITCH_STATES } from './enums.js';
import { memberSummarySchema } from './members.js';
import { movieSchema } from './movies.js';
import { DEFAULT_TITLE_ORDER, NO_TITLE, movieTitle } from './titles.js';
/**
 * The Pitch Room and the wildcard slot (DESIGN.md §12).
 *
 * Two objects that meet exactly once. A **pitch** is an index card on a
 * corkboard: somebody's film, 150 characters of argument, and a running score.
 * A **wildcard slot** is a screening with a date and no film. At the slot's
 * `pitchClosesAt` the standings are read once and the winner is bound to it.
 *
 * ## The corkboard never closes
 *
 * "Voting closes 6 hours before showtime" is a property of the *slot*, not of
 * the board: a slot takes a snapshot, and every pitch that did not win is still
 * pinned up the next morning ("Pitches persist", §12). This matters because it
 * decides what the close time is allowed to do — it freezes an outcome, it does
 * not stop anybody pitching or voting. A board that shut itself down between
 * slots would be empty exactly when somebody thinks of something.
 *
 * Only pitches pinned **before** the close count towards that slot, which is
 * what stops a late card from winning a vote it was never in.
 */
export const pitchStateSchema = z.enum(PITCH_STATES);
/**
 * The constraint is the feature (§12). Enforced three times on purpose: the
 * textarea's counter, this schema, and `pitches_text_len_check` in SQL.
 */
export const PITCH_TEXT_MAX = 150;
/** Only for a pitch with no film attached — the "what is it called" line. */
export const PITCH_TITLE_MAX = 120;
/**
 * How many open pitches one member may have up at once.
 *
 * Not in the design doc. It is here because a corkboard is a shared surface
 * with a finite amount of room on it, and one enthusiastic person with eleven
 * cards is the failure mode that makes everyone else stop reading it. Five is
 * a guess; it is a single number in a single place, so it is a cheap guess to
 * revise. Withdrawing a card gives you the slot back.
 */
export const MAX_OPEN_PITCHES_PER_USER = 5;
/** The convention the admin panel offers when opening a slot (§12). */
export const WILDCARD_CLOSE_LEAD_HOURS = 6;
/**
 * **One dislike per member, across the whole board.**
 *
 * Upvotes are cheap and unlimited — one per card, as many cards as you like.
 * The downvote is not: you get exactly one, ever, and putting it on a card
 * takes it off whatever it was on before.
 *
 * The reason is what the old rule turned into. With unlimited downvotes the
 * winning move is to upvote your own film and downvote everything else, and
 * once two people work that out the score stops meaning "what does the room
 * want" and starts meaning "who bothered". One sticker turns the downvote into
 * the only thing it should ever have been: *I really do not want to watch
 * this*. Spending it on a film you merely dislike means having nothing left for
 * the one you would leave the room over.
 *
 * It is a sticker in the UI rather than an arrow for the same reason — an arrow
 * is a vote you cast, a sticker is an object you own, and an object is
 * obviously somewhere rather than nowhere.
 *
 * Enforced in the vote transaction rather than by a unique index: the rule is
 * "one dislike among **open** pitches", and an index predicate cannot see
 * `pitches.state`. A dislike on a card that has since won or come down is
 * inert, and must not cost you your sticker.
 */
export const MAX_DISLIKES_PER_USER = 1;
/**
 * "Contested pitches (high traffic both ways) get a visual tell — a card so
 * covered in pins it's shredded" (§12).
 *
 * Contested is not "unpopular", and it is not "close". It is *both sides
 * turned up*: a card at +1 from a single vote is not interesting, and a card at
 * +1 from nine up and eight down is the argument the room is actually having.
 * So both thresholds have to be met, and the numbers live here rather than in
 * a CSS class so the room and anything that shows a pitch later agree.
 *
 * Three dislikes means much more since each member only has one to spend
 * (see `MAX_DISLIKES_PER_USER`): a card three people have each spent their
 * only sticker on is not close, it is a fight.
 */
export const PITCH_CONTESTED_MIN_EACH = 3;
export const PITCH_CONTESTED_MAX_GAP = 2;
/**
 * The pushpin, chosen by whoever wrote the card.
 *
 * It decides nothing. That is the point: a corkboard is a shared surface and
 * this is the only mark on it that is *yours* — the one thing on a pitch that
 * is not an argument, a score or a deadline. Cheap to store, impossible to
 * misuse, and it makes a board of four cards look like four people.
 *
 * Both halves travel together or not at all: a colour with no shape is not a
 * pin. `pitches_pin_check` says so in SQL, and cards pinned before the picker
 * existed carry null and fall back to a colour hashed from their id.
 */
export const PIN_COLORS = ['red', 'orange', 'yellow', 'green', 'blue', 'purple'];
export const PIN_SHAPES = ['round', 'square', 'diamond', 'star'];
export const pinSchema = z.object({
    color: z.enum(PIN_COLORS),
    shape: z.enum(PIN_SHAPES),
});
/** Serbian, for the picker's labels. Not on the wire — the slug is. */
export const PIN_COLOR_NAMES = {
    red: 'crvena',
    orange: 'narandžasta',
    yellow: 'žuta',
    green: 'zelena',
    blue: 'plava',
    purple: 'ljubičasta',
};
export const PIN_SHAPE_NAMES = {
    round: 'okrugla',
    square: 'kvadratna',
    diamond: 'romb',
    star: 'zvezda',
};
/**
 * A pin nobody chose.
 *
 * Used for cards pinned before the picker existed, and as the composer's
 * starting point so that a member who ignores the picker entirely still gets a
 * board that looks pinned by hand rather than forty identical red dots. Seeded
 * from the id so it never changes under a re-render — the same rule the card's
 * tilt follows, and for the same reason.
 */
export function defaultPin(seed) {
    return {
        color: PIN_COLORS[Math.floor(seed * PIN_COLORS.length) % PIN_COLORS.length],
        shape: 'round',
    };
}
export const pitchSchema = z.object({
    id: z.string().uuid(),
    state: pitchStateSchema,
    /** ISO 8601, UTC. Also the tie-break, so it travels rather than being sorted away. */
    createdAt: z.string(),
    by: memberSummarySchema,
    /** The 150 characters. The argument, not the title. */
    text: z.string(),
    /** Set when the pitch names a film we have a row for; the card shows a poster. */
    movie: movieSchema.nullable(),
    /** The other kind of pitch: a title, or a wish, with no film attached yet. */
    freeTitle: z.string().nullable(),
    up: z.number().int().nonnegative(),
    down: z.number().int().nonnegative(),
    /** `up - down`, computed server-side so nothing has to agree about arithmetic. */
    score: z.number().int(),
    /**
     * Your vote: 1, -1, or null for none. Resolved server-side from the session
     * for the same reason `hype.mine` is — a client that has to find itself in a
     * list is a client that can get it wrong.
     */
    mine: z.union([z.literal(1), z.literal(-1)]).nullable(),
    /** You wrote it. Only the author (and the host) may withdraw a card. */
    isMine: z.boolean(),
    /** Null on cards pinned before the picker existed; the card hashes one. */
    pin: pinSchema.nullable(),
    /** The slot it won, once it has won one. */
    screeningId: z.string().uuid().nullable(),
});
/**
 * A slot the corkboard is currently playing for.
 *
 * The room shows these at the top: a pitch with nothing to win is just an
 * opinion, and "glasanje se zatvara za 4 sata" is the only thing on this screen
 * that is urgent.
 */
export const pitchSlotSchema = z.object({
    screeningId: z.string().uuid(),
    startsAt: z.string(),
    closesAt: z.string().nullable(),
    roomName: z.string(),
});
export const pitchBoardResponse = z.object({
    /** The server's clock, so "zatvara se za…" does not depend on the phone's. */
    now: z.string(),
    pitches: z.array(pitchSchema),
    slots: z.array(pitchSlotSchema),
    /** How many more cards you may pin. Saves the composer from guessing. */
    remaining: z.number().int().nonnegative(),
});
/**
 * Pinning a card.
 *
 * Three ways to name the film, and exactly one of them per pitch:
 *
 *  - `tmdbId` — picked from search. The server imports it (idempotent on
 *    `tmdb_id`) so the card can show a poster served from our own origin.
 *  - `movieId` — already in the catalogue. No network at all.
 *  - `freeTitle` — no film row, and a completely normal answer. "Nešto sa
 *    Nicolasom Kejdžom" is a pitch; it just cannot be bound to a slot until
 *    somebody decides which film it actually is.
 */
export const createPitchRequest = z
    .object({
    text: z
        .string()
        .trim()
        .min(1, 'Napiši nešto.')
        .max(PITCH_TEXT_MAX, `Najviše ${PITCH_TEXT_MAX} karaktera.`),
    tmdbId: z.number().int().positive().nullable().default(null),
    movieId: z.string().uuid().nullable().default(null),
    freeTitle: z.string().trim().min(1).max(PITCH_TITLE_MAX).nullable().default(null),
    /** Optional, because a pitch is about a film and not about stationery. */
    pin: pinSchema.nullable().default(null),
})
    .superRefine((value, ctx) => {
    const named = [value.tmdbId, value.movieId, value.freeTitle].filter((part) => part !== null).length;
    if (named === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['freeTitle'],
            message: 'Reci koji film — izaberi ga ili ukucaj naslov.',
        });
    }
    if (named > 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['freeTitle'],
            message: 'Jedan film po cedulji.',
        });
    }
});
/**
 * Your vote on one card, as a PUT rather than a toggle.
 *
 * The hype flame is a toggle because it has one bit of state and the client can
 * be stale about it. This has three, so "flip it" is not a question with an
 * answer — tapping the lit up-arrow means *no vote*, and moving the sticker
 * here means *-1 here and nowhere else*, and both of those are statements the
 * client can make exactly. Idempotent, which also makes a retry after a dropped
 * response harmless.
 */
export const votePitchRequest = z.object({
    value: z.union([z.literal(1), z.literal(0), z.literal(-1)]),
});
/** One card's tallies. The shape both halves of a moved sticker come back in. */
export const pitchTallySchema = z.object({
    pitchId: z.string().uuid(),
    up: z.number().int().nonnegative(),
    down: z.number().int().nonnegative(),
    score: z.number().int(),
});
export const pitchVoteResponse = z.object({
    up: z.number().int().nonnegative(),
    down: z.number().int().nonnegative(),
    score: z.number().int(),
    mine: z.union([z.literal(1), z.literal(-1)]).nullable(),
    /**
     * The card the sticker came **off**, when placing it moved it.
     *
     * Two cards change on one request, and the client has to un-stick the old one
     * — so the response says which, with its corrected tallies. Null for an
     * upvote, for clearing, and for a dislike that was not already somewhere.
     */
    moved: pitchTallySchema.nullable(),
});
/**
 * The standings order, and the tie-break that decides a slot.
 *
 * **Earliest pinned wins a tie** (§12, open question 2) — it rewards
 * conviction over last-minute campaigning. It lives here, exported, because
 * three things have to agree about it: the SQL that picks a winner, the order
 * the room renders, and any client that re-sorts after a vote lands. A
 * comparator in one place is how they stay agreed.
 */
export function comparePitches(a, b) {
    if (a.score !== b.score)
        return b.score - a.score;
    return Date.parse(a.createdAt) - Date.parse(b.createdAt);
}
/** Both sides turned up, and neither won. See the thresholds above. */
export function isContested(pitch) {
    return (pitch.up >= PITCH_CONTESTED_MIN_EACH &&
        pitch.down >= PITCH_CONTESTED_MIN_EACH &&
        Math.abs(pitch.up - pitch.down) <= PITCH_CONTESTED_MAX_GAP);
}
/**
 * What the card is called. The film's title in the reader's own language order
 * when a film is attached, the free title otherwise — never both, by
 * construction (see `createPitchRequest`).
 *
 * The free title is whatever the author typed and is not translated anywhere:
 * "nešto sa Belušijem" is not a film, it is a wish, and it has exactly one
 * language.
 */
export function pitchTitle(pitch, order = DEFAULT_TITLE_ORDER) {
    if (pitch.movie)
        return movieTitle(pitch.movie, order);
    return pitch.freeTitle ?? NO_TITLE;
}
/**
 * Whether a pitch could actually be bound to a slot if it won tonight.
 *
 * A free-title pitch can win the vote and still not become a film row — there
 * is nothing to set `screenings.movie_id` to. That is not a bug to design
 * around; it is the honest state of "the room voted for an idea". The room uses
 * this to say so on the card *before* the vote closes, rather than surprising
 * everybody at the reveal.
 */
export function canFillSlot(pitch) {
    return pitch.movie !== null;
}
//# sourceMappingURL=pitches.js.map